/* ─────────────────────────────────────────────────────────────
   ModoDemoBanner.jsx — Banner naranja superior + botón admin
   ───────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useModoDemoEstado() {
  const { user } = useAuth();
  const [estado, setEstado] = useState(null);
  const cargar = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/admin/modo-demo/estado`);
      setEstado(r.data);
    } catch {}
  };
  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, [user]);
  return { estado, recargar: cargar };
}

export default function ModoDemoBanner({ verDemo, setVerDemo }) {
  const { estado } = useModoDemoEstado();
  if (!estado?.activo) return null;
  const fechaExp = estado.fecha_expiracion ? new Date(estado.fecha_expiracion) : null;
  return (
    <div className="modo-demo-banner" data-testid="modo-demo-banner">
      <div className="mdb-left">
        <i className="fa-solid fa-flask-vial" />
        <strong>MODO DEMOSTRACIÓN ACTIVO</strong> · Datos sintéticos.
        Activado por <strong>{estado.usuario_nombre}</strong>
        {fechaExp && <> · Expira: <strong>{fechaExp.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}</strong></>}
        {!fechaExp && <> · <em>sin expiración (manual)</em></>}
      </div>
      <label className="mdb-toggle" data-testid="toggle-ver-demo">
        <input type="checkbox" checked={verDemo} onChange={(e) => setVerDemo(e.target.checked)} />
        <span>Ver datos demo</span>
      </label>
    </div>
  );
}

/* ─── Botón admin con modal de duración ─── */
export function ModoDemoAdminBtn({ onCambio }) {
  const { user } = useAuth();
  const { estado, recargar } = useModoDemoEstado();
  const [showModal, setShowModal] = useState(false);
  const [showLimpiar, setShowLimpiar] = useState(false);
  const [duracion, setDuracion] = useState("4h");
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user?.rol !== "admin") return null;

  const activar = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/modo-demo/activar`, { duracion });
      toast.success(`Modo demo activado (${duracion}) — ${r.data.registros_creados.intervenciones} intervenciones`);
      setShowModal(false);
      await recargar();
      onCambio?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const limpiar = async () => {
    if (!confirmCheck) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/modo-demo/limpiar`, { confirmacion: true });
      const e = r.data.registros_eliminados;
      toast.success(`Demo limpiado: ${e.intervenciones} interv · ${e.intervencion_vueltas} vueltas · ${e.intervencion_fotos} fotos`);
      setShowLimpiar(false);
      setConfirmCheck(false);
      await recargar();
      onCambio?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="modo-demo-admin" data-testid="admin-modo-demo">
        <h3>🧪 Modo Demostración</h3>
        {estado?.activo ? (
          <>
            <div className="text-sm mb-2 text-emerald-700">
              <i className="fa-solid fa-circle-check" /> Activo
              {estado.fecha_expiracion && <> hasta {new Date(estado.fecha_expiracion).toLocaleString("es-MX")}</>}
            </div>
            <button className="btn-danger" onClick={() => setShowLimpiar(true)} data-testid="btn-limpiar-demo">
              <i className="fa-solid fa-broom" /> Limpiar demo manualmente
            </button>
          </>
        ) : (
          <>
            <div className="text-sm mb-2 text-slate-500">Sin sesión de demo activa</div>
            <button className="btn-primary" onClick={() => setShowModal(true)} data-testid="btn-activar-demo">
              <i className="fa-solid fa-flask" /> Activar Modo Demo
            </button>
          </>
        )}
      </div>

      {/* Modal duración */}
      {showModal && (
        <div className="md-modal-bg" onClick={() => setShowModal(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()} data-testid="modal-activar-demo">
            <h2>Duración del Modo Demostración:</h2>
            <div className="md-radios">
              {[
                ["1h", "1 hora", "demo rápida"],
                ["4h", "4 horas", "presentación / junta larga ★ recomendado para demo Rubén"],
                ["1d", "1 día", "entrenamiento supervisores"],
                ["1w", "1 semana", "período de prueba con choferes piloto"],
                ["manual", "Sin expiración", "manual — yo lo apago"],
              ].map(([val, lbl, hint]) => (
                <label key={val} className="md-radio">
                  <input type="radio" name="dur" value={val} checked={duracion === val} onChange={(e) => setDuracion(e.target.value)} data-testid={`radio-${val}`} />
                  <div>
                    <div className="md-radio-lbl">{lbl}</div>
                    <div className="md-radio-hint">{hint}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="md-foot">
              <button className="btn-ghost-sm" onClick={() => setShowModal(false)} disabled={busy}>Cancelar</button>
              <button className="btn-primary" onClick={activar} disabled={busy} data-testid="btn-confirmar-activar">
                {busy ? "Activando…" : "Activar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal limpiar */}
      {showLimpiar && (
        <div className="md-modal-bg" onClick={() => setShowLimpiar(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()} data-testid="modal-limpiar-demo">
            <h2 className="text-red-600"><i className="fa-solid fa-triangle-exclamation" /> ¿Estás seguro?</h2>
            <p>Esto eliminará <strong>TODOS</strong> los registros con <code>es_demo=true</code>.</p>
            <label className="md-check" data-testid="check-confirm-limpiar">
              <input type="checkbox" checked={confirmCheck} onChange={(e) => setConfirmCheck(e.target.checked)} />
              <span>Entiendo que esta acción es permanente</span>
            </label>
            <div className="md-foot">
              <button className="btn-ghost-sm" onClick={() => { setShowLimpiar(false); setConfirmCheck(false); }} disabled={busy}>Cancelar</button>
              <button className="btn-danger" onClick={limpiar} disabled={!confirmCheck || busy} data-testid="btn-confirmar-limpiar">
                {busy ? "Limpiando…" : "Sí, limpiar todo lo demo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
